/**
 * Verifactu invoice PDF template.
 *
 * Renders a legal Spanish invoice with the mandatory Veri*Factu marks:
 *   - QR code (base64 PNG, provided by Verifacti on emission)
 *   - "VERI*FACTU" legend adjacent to the QR
 *   - All RD 1619/2012 fields: issuer, recipient, lines, VAT breakdown,
 *     total, issue date, series + number.
 *
 * The hash is NOT rendered (optional for Veri*Factu, the QR suffices).
 * The XML itself lives at the AEAT — we never render nor attach it.
 */

import { FiscalProfile, InvoiceDraft, InvoiceLine } from '@cuadrilla/types/invoice';
import type { EmitResponse } from '../sync/invoicing';

export interface BuildInvoicePdfArgs {
  profile: FiscalProfile;
  draft: InvoiceDraft;
  response: EmitResponse;
  locale: 'es' | 'en';
  /**
   * Spec 218 PR5 — when true, overlay an "ANULADA" watermark across
   * the whole page so a downloaded PDF of a cancelled invoice cannot
   * be confused with the original. Mobile uses the same template but
   * never offers to download a cancelled invoice today; the web does
   * via /invoices/:id, so we need this flag here.
   */
  cancelled?: boolean;
}

const COLORS = {
  primary: '#1B4F72',
  ink: '#0F1923',
  muted: '#475569',
  line: '#E2E8F0',
  hairline: '#CBD5E1',
  softBg: '#F8FAFC',
  zebra: '#F5F7FA',
};

function esc(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatAmount(n: number): string {
  return n.toFixed(2).replace('.', ',') + ' €';
}

function formatDate(iso: string, locale: 'es' | 'en'): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function recipientBlock(draft: InvoiceDraft, locale: 'es' | 'en'): string {
  const r = draft.recipient;
  if (r.kind === 'none') {
    return `<p style="color:${COLORS.muted}; font-style:italic">${locale === 'es' ? 'Destinatario no identificado (F2)' : 'Unidentified recipient (F2)'}</p>`;
  }
  const lines: string[] = [];
  if (r.name) lines.push(`<strong>${esc(r.name)}</strong>`);
  if (r.kind === 'national' && r.nif) lines.push(`NIF: ${esc(r.nif)}`);
  if (r.kind !== 'national') {
    const parts = [r.countryCode, r.foreignIdType, r.foreignId].filter(Boolean).map(esc);
    if (parts.length) lines.push(parts.join(' · '));
  }
  if (r.address) lines.push(esc(r.address));
  if (r.email) lines.push(esc(r.email));
  return lines.map(l => `<div>${l}</div>`).join('');
}

function lineRow(line: InvoiceLine, idx: number): string {
  const total = line.base + line.vatAmount;
  return `
    <tr class="${idx % 2 === 0 ? 'zebra' : ''}">
      <td class="desc">${esc(line.description)}</td>
      <td class="num">${formatAmount(line.base)}</td>
      <td class="num">${line.vatRate}%</td>
      <td class="num">${formatAmount(line.vatAmount)}</td>
      <td class="num total">${formatAmount(total)}</td>
    </tr>
  `;
}

export function buildInvoicePdfHtml(args: BuildInvoicePdfArgs): string {
  const { profile, draft, response, locale, cancelled } = args;
  const totalBase = draft.lines.reduce((s, l) => s + l.base, 0);
  const totalVat = draft.lines.reduce((s, l) => s + l.vatAmount, 0);
  const total = totalBase + totalVat;

  const qrSrc = response.qrBase64 ? `data:image/png;base64,${response.qrBase64}` : '';

  const labels = locale === 'es' ? {
    invoice: 'FACTURA',
    issuer: 'Emisor',
    recipient: 'Cliente',
    series: 'Serie / Nº',
    issueDate: 'Fecha',
    concept: 'Concepto',
    base: 'Base',
    vatRate: 'IVA',
    vatAmount: 'Cuota',
    lineTotal: 'Importe',
    totalBase: 'Base imponible',
    totalVat: 'Total IVA',
    totalAmount: 'Total',
    legend: 'Factura verificable en la sede electrónica de la AEAT',
    description: 'Descripción',
  } : {
    invoice: 'INVOICE',
    issuer: 'Issuer',
    recipient: 'Client',
    series: 'Series / No.',
    issueDate: 'Date',
    concept: 'Concept',
    base: 'Base',
    vatRate: 'VAT',
    vatAmount: 'VAT amt',
    lineTotal: 'Amount',
    totalBase: 'Taxable base',
    totalVat: 'Total VAT',
    totalAmount: 'Total',
    legend: 'Invoice verifiable at the AEAT electronic office',
    description: 'Description',
  };

  return `<!DOCTYPE html>
<html lang="${locale}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${labels.invoice} ${esc(draft.series)} ${esc(draft.number)}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: -apple-system, Roboto, "Helvetica Neue", sans-serif;
           color: ${COLORS.ink}; font-size: 11px; margin: 0; }
    .header {
      display: flex; justify-content: space-between; align-items: flex-start;
      border-bottom: 2px solid ${COLORS.primary}; padding-bottom: 10px; margin-bottom: 16px;
    }
    .docTitle { font-size: 22px; font-weight: 700; color: ${COLORS.primary}; letter-spacing: 2px; }
    .docMeta { text-align: right; font-size: 11px; color: ${COLORS.muted}; }
    .docMeta strong { color: ${COLORS.ink}; font-size: 13px; }
    .parties { display: flex; gap: 16px; margin-bottom: 20px; }
    .party {
      flex: 1; border: 1px solid ${COLORS.line}; border-radius: 6px;
      padding: 10px 12px; background: ${COLORS.softBg};
    }
    .party h3 { margin: 0 0 6px; font-size: 10px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 1px; }
    .party div { line-height: 1.4; }
    table.lines { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    table.lines th {
      text-align: left; background: ${COLORS.primary}; color: #fff;
      padding: 8px; font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase;
    }
    table.lines td { padding: 8px; border-bottom: 1px solid ${COLORS.line}; vertical-align: top; }
    table.lines td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    table.lines td.total { font-weight: 600; }
    table.lines tr.zebra td { background: ${COLORS.zebra}; }
    .totals { display: flex; justify-content: flex-end; margin-bottom: 20px; }
    .totalsBox {
      min-width: 240px; border: 1px solid ${COLORS.line}; border-radius: 6px;
      padding: 10px 14px; background: #fff;
    }
    .totalsBox .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; }
    .totalsBox .row.grand {
      border-top: 2px solid ${COLORS.primary}; padding-top: 8px; margin-top: 4px;
      font-size: 14px; font-weight: 700; color: ${COLORS.primary};
    }
    .footer {
      display: flex; justify-content: space-between; align-items: flex-end;
      border-top: 1px solid ${COLORS.line}; padding-top: 16px; margin-top: 24px;
    }
    .qrBlock { text-align: center; }
    .qrBlock img { width: 110px; height: 110px; display: block; margin: 0 auto 6px; }
    .qrBlock .mark { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; color: ${COLORS.primary}; }
    .qrBlock .legend { font-size: 9px; color: ${COLORS.muted}; max-width: 200px; margin-top: 4px; }
    .qrBlock .url { font-size: 8px; color: ${COLORS.muted}; margin-top: 3px; word-break: break-all; max-width: 240px; }
    .iban { font-size: 10px; color: ${COLORS.muted}; text-align: left; }
    .description { margin-bottom: 14px; padding: 10px; background: ${COLORS.softBg};
                    border-left: 3px solid ${COLORS.primary}; border-radius: 4px; font-size: 11px; }
    .cancelled-watermark {
      position: fixed;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 120px;
      font-weight: 900;
      color: rgba(220, 38, 38, 0.18);
      letter-spacing: 12px;
      pointer-events: none;
      z-index: 100;
      white-space: nowrap;
    }
    .cancelled-banner {
      background: #DC2626;
      color: #fff;
      text-align: center;
      padding: 6px 12px;
      font-weight: 700;
      letter-spacing: 2px;
      margin-bottom: 12px;
      border-radius: 4px;
    }
  </style>
</head>
<body>
  ${cancelled ? `<div class="cancelled-watermark" data-testid="cancelled-watermark">${locale === 'es' ? 'ANULADA' : 'CANCELLED'}</div><div class="cancelled-banner">${locale === 'es' ? 'FACTURA ANULADA' : 'INVOICE CANCELLED'}</div>` : ''}
  <div class="header">
    <div>
      <div class="docTitle">${labels.invoice}</div>
      <div style="color: ${COLORS.muted}; font-size: 10px; margin-top: 2px;">${esc(draft.type)} · Veri*Factu</div>
    </div>
    <div class="docMeta">
      <div>${labels.series}</div>
      <div><strong>${esc(draft.series)} · ${esc(draft.number)}</strong></div>
      <div style="margin-top: 6px;">${labels.issueDate}</div>
      <div><strong>${formatDate(draft.issueDate, locale)}</strong></div>
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <h3>${labels.issuer}</h3>
      <div><strong>${esc(profile.legalName)}</strong></div>
      <div>NIF: ${esc(profile.nif)}</div>
      <div>${esc(profile.address)}</div>
      <div>${esc(profile.postalCode)} ${esc(profile.city)}${profile.province ? ' (' + esc(profile.province) + ')' : ''}</div>
    </div>
    <div class="party">
      <h3>${labels.recipient}</h3>
      ${recipientBlock(draft, locale)}
    </div>
  </div>

  ${draft.description ? `<div class="description">${esc(draft.description)}</div>` : ''}

  <table class="lines">
    <thead>
      <tr>
        <th>${labels.concept}</th>
        <th style="text-align:right">${labels.base}</th>
        <th style="text-align:right">${labels.vatRate}</th>
        <th style="text-align:right">${labels.vatAmount}</th>
        <th style="text-align:right">${labels.lineTotal}</th>
      </tr>
    </thead>
    <tbody>
      ${draft.lines.map(lineRow).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totalsBox">
      <div class="row"><span>${labels.totalBase}</span><span>${formatAmount(totalBase)}</span></div>
      <div class="row"><span>${labels.totalVat}</span><span>${formatAmount(totalVat)}</span></div>
      <div class="row grand"><span>${labels.totalAmount}</span><span>${formatAmount(total)}</span></div>
    </div>
  </div>

  <div class="footer">
    ${profile.iban ? `<div class="iban"><strong>IBAN:</strong> ${esc(profile.iban)}</div>` : '<div></div>'}
    <div class="qrBlock">
      ${qrSrc ? `<img src="${qrSrc}" alt="QR" />` : ''}
      <div class="mark">VERI*FACTU</div>
      <div class="legend">${labels.legend}</div>
      ${response.qrUrl ? `<div class="url">${esc(response.qrUrl)}</div>` : ''}
    </div>
  </div>
</body>
</html>`;
}
